(function (root) {
  'use strict';

  // Keep this list aligned with the countries enabled for the store's markets.
  // Countries not in the list are left untouched rather than assigned a wrong code.
  const COUNTRY_DIAL_CODES = Object.freeze({
    AE: '+971',
    AT: '+43',
    AU: '+61',
    BE: '+32',
    CA: '+1',
    CH: '+41',
    CN: '+86',
    CZ: '+420',
    DE: '+49',
    DK: '+45',
    ES: '+34',
    FI: '+358',
    FR: '+33',
    GB: '+44',
    HK: '+852',
    IE: '+353',
    IL: '+972',
    IN: '+91',
    IT: '+39',
    JP: '+81',
    KR: '+82',
    MY: '+60',
    NL: '+31',
    NO: '+47',
    NZ: '+64',
    PT: '+351',
    SE: '+46',
    SG: '+65',
    US: '+1',
    VN: '+84'
  });

  const NATIONAL_TRUNK_PREFIXES = Object.freeze({
    AE: '0',
    AU: '0',
    BE: '0',
    CH: '0',
    CN: '0',
    CZ: '0',
    DE: '0',
    DK: '0',
    ES: '0',
    FI: '0',
    FR: '0',
    GB: '0',
    HK: '0',
    IE: '0',
    IL: '0',
    IN: '0',
    JP: '0',
    KR: '0',
    MY: '0',
    NL: '0',
    NO: '0',
    NZ: '0',
    PT: '0',
    SE: '0',
    SG: '0',
    VN: '0'
  });

  function getDialCode(countryCode) {
    if (!countryCode) return '';

    const normalizedCode = String(countryCode).trim().toUpperCase();
    if (/^\+\d{1,4}$/.test(normalizedCode)) return normalizedCode;

    return COUNTRY_DIAL_CODES[normalizedCode] || '';
  }

  function getTrunkPrefix(countryCode, dialCode) {
    const normalizedCountryCode = String(countryCode || '').trim().toUpperCase();
    if (NATIONAL_TRUNK_PREFIXES[normalizedCountryCode]) {
      return NATIONAL_TRUNK_PREFIXES[normalizedCountryCode];
    }

    const matchingCountry = Object.keys(COUNTRY_DIAL_CODES).find((code) => (
      COUNTRY_DIAL_CODES[code] === dialCode && NATIONAL_TRUNK_PREFIXES[code]
    ));

    return matchingCountry ? NATIONAL_TRUNK_PREFIXES[matchingCountry] : '';
  }

  function applyCountryCode(input, countryCode) {
    if (!input || String(input.value || '').trim() !== '') return false;

    const dialCode = getDialCode(countryCode);
    if (!dialCode) return false;

    input.value = `${dialCode} `;
    if (input.dataset) input.dataset.autoCountryCode = dialCode;

    return true;
  }

  function normalizePhoneInput(input, countryCode) {
    if (!input) return false;

    const dialCode = getDialCode(countryCode);
    if (!dialCode) return false;

    const value = String(input.value || '').trim();
    if (!value) return applyCountryCode(input, dialCode);
    if (/^\s*00/.test(value)) return false;

    const trunkPrefix = getTrunkPrefix(countryCode, dialCode);
    let normalizedValue = value;

    if (/^\s*\+/.test(value)) {
      const internationalValue = `+${value.slice(1).replace(/\D/g, '')}`;
      if (!internationalValue.startsWith(dialCode)) return false;

      const nationalNumber = internationalValue.slice(dialCode.length);
      const withoutTrunkPrefix = trunkPrefix && nationalNumber.startsWith(trunkPrefix)
        ? nationalNumber.slice(trunkPrefix.length)
        : nationalNumber;
      normalizedValue = `${dialCode}${withoutTrunkPrefix}`;
    } else {
      const nationalNumber = value.replace(/\D/g, '');
      const withoutTrunkPrefix = trunkPrefix && nationalNumber.startsWith(trunkPrefix)
        ? nationalNumber.slice(trunkPrefix.length)
        : nationalNumber;
      normalizedValue = `${dialCode}${withoutTrunkPrefix}`;
    }

    if (normalizedValue === input.value) return false;
    input.value = normalizedValue;
    if (input.dataset) input.dataset.autoCountryCode = dialCode;
    return true;
  }

  async function getCountryFromShopify(fetchImpl, currentCountry = '', routesRoot = '/') {
    if (typeof fetchImpl !== 'function') return '';

    try {
      const endpoint = `${routesRoot}browsing_context_suggestions.json?country%5Benabled%5D=true&country%5Bexclude%5D=${encodeURIComponent(currentCountry)}`;
      const response = await fetchImpl(endpoint, {
        headers: { Accept: 'application/json' }
      });

      if (!response || !response.ok) return '';

      const context = await response.json();
      const detectedCountry = context
        && context.detected_values
        && context.detected_values.country
        && context.detected_values.country.handle;
      const suggestedCountry = context
        && context.suggestions
        && context.suggestions[0]
        && context.suggestions[0].parts
        && context.suggestions[0].parts.country
        && context.suggestions[0].parts.country.handle;

      return getDialCode(detectedCountry || suggestedCountry);
    } catch (_error) {
      return '';
    }
  }

  async function initialize(documentRef, fetchImpl) {
    const form = documentRef && documentRef.querySelector('#trade-form');
    const input = form && form.querySelector('#Company_Phone');

    if (!input) return;

    let detectedDialCode = '';
    const handlePhoneInput = () => normalizePhoneInput(input, detectedDialCode);
    if (typeof input.addEventListener === 'function') {
      input.addEventListener('input', handlePhoneInput);
      input.addEventListener('blur', handlePhoneInput);
    }

    const request = fetchImpl || (root && typeof root.fetch === 'function'
      ? root.fetch.bind(root)
      : null);
    const shopify = root && root.Shopify ? root.Shopify : {};
    const routesRoot = shopify.routes && shopify.routes.root
      ? shopify.routes.root
      : '/';
    detectedDialCode = await getCountryFromShopify(
      request,
      shopify.country || '',
      routesRoot
    );
    normalizePhoneInput(input, detectedDialCode);

  }

  const api = {
    applyCountryCode,
    getCountryFromShopify,
    getDialCode,
    initialize,
    normalizePhoneInput
  };

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }

  if (root && root.document) {
    root.TradePhoneCountry = api;
    if (root.document.readyState === 'loading') {
      root.document.addEventListener('DOMContentLoaded', () => initialize(root.document));
    } else {
      initialize(root.document);
    }
  }
})(typeof window !== 'undefined' ? window : null);
